import { type ReactElement, useCallback } from 'react';
import classNames from 'classnames';

import { Link } from 'react-router';

import { useAppDispatch, useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme } from '@prose-ui/legacy';
import { makeStyles } from 'legacyStyles';

import { Button } from 'Components/LegacyButton';

import { productsSlugs } from 'constants/products';

import { cta } from 'assets/content/landingPages/supplements';

import { trackPreselect } from 'dux/tracking/actions';
import { getIsAuthenticating } from 'dux/auth/selectors';
import { getSupplementsCTAProps } from 'dux/userFlows/selectors';

const useStyles = makeStyles({
  button: {
    margin: 0,
    lineHeight: '59px',

    [legacyTheme.breakpoints.up('sm')]: {
      width: '300px',
      height: '55px',
      lineHeight: '61px',
    },
  },
  buttonLarge: {
    [legacyTheme.breakpoints.up('md')]: {
      width: '370px',
    },
  },
  buttonWhite: {
    borderColor: `${theme.colors.primary[400]}`,
    '&:hover': {
      backgroundColor: `${theme.colors.primary[400]}`,
      color: `${theme.colors.neutral[100]}`,
    },
  },
});

type CTAProps = {
  buttonVariant?: 'white' | 'whiteFull';
  large?: boolean;
  label?: string;
  children?: ({ dataClick, to }: { dataClick: string; to: string }) => ReactElement;
  override?: boolean;
};

export const CTA = ({
  buttonVariant = 'whiteFull',
  children,
  large = false,
  label = cta,
  override = false,
}: CTAProps) => {
  const isAuthenticating = useAppSelector(getIsAuthenticating);
  const ctaProps = useAppSelector(getSupplementsCTAProps);

  const dispatch = useAppDispatch();

  const { classes } = useStyles();

  const onClick = useCallback(() => {
    dispatch(
      // @ts-expect-error trackPreselect is not typed yet
      trackPreselect({
        productSlug: productsSlugs.SUPPLEMENT_CORE,
        locationId: 'landing-page_supplement',
      }),
    );
  }, [trackPreselect]);

  if (isAuthenticating) {
    return null;
  }
  const { dataClick, to } = ctaProps;
  if (children) {
    return children({ dataClick, to });
  }

  return (
    <Button
      classes={{
        root: classNames(classes.button, {
          [classes.buttonLarge ?? '']: large,
          [classes.buttonWhite ?? '']: buttonVariant.includes('white'),
        }),
      }}
      Component={Link}
      data-click={dataClick}
      data-from="supplements-page"
      data-testid="supplements-cta"
      onClick={onClick}
      to={to}
      variant={buttonVariant}
    >
      {override ? label : cta}
    </Button>
  );
};
